import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { YearService } from '../../../../../shared-components/year/year.service';
import { StudentService } from '../../../../../shared-components/student/student.service';
import { StudentSummaryService } from '../../../student-summary.service';
import { CardStudentGrades } from './card-student-grades';
import { Card } from '../../../card-container/card/card';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';
import { interval, Subscription } from 'rxjs';
import { LoginService } from '../../../../../shared-modules/login/login.service';
import { AssignmentService } from '../../../../assignment/assignment.service';
import { CourseSchedule } from '../../../../../shared-components/student/course-schedule.model';
import { Student360Service } from '../../../../student360.service';
import { Classroom360Service } from '../../../../../classroom360/classroom360.service';
import { UserService } from '../../../../../shared-components/user/user.service';
import { TylerMatTableDataSource } from '../../../../../shared-components/angular-material/extended.class';
import { detailExpand } from '../../../../../shared-components/angular-material/mat-animations';
import { LoginSettings } from '../../../../../shared-modules/login/LoginSetting.model';

@AutoUnsubscribe({
    includeArrays: true
})

@Component({
    selector: 'app-current-grades-card',
    templateUrl: './current-grades-card.component.html',
    styleUrls: ['./current-grades-card.component.css'],
    // Animation for when the detail row is expanded or collapsed
    animations: [detailExpand]
})

export class CurrentGradesCardComponent implements OnInit, OnDestroy {
    @Input() data: any;
    card: Card;
    studentGrades: CardStudentGrades[] = [];
    currentTerm: string;
    gradingPeriodId = 0;
    loginSettings: LoginSettings;
    selectedCourse = new CourseSchedule();
    parentPortalSubscription: Subscription;
    staffAssignedPortalsSubscription: Subscription;
    showAssignmentLink = false;
    // ----------Angular Material------------------------------------
    angularMatData = new TylerMatTableDataSource();
    displayedColumns: string[] = ['grade', 'courseName', 'warning', 'tardy', 'absent', 'more'];
    colsLength = 4;

    constructor(private yearService: YearService,
        private userService: UserService,
        private studentService: StudentService,
        private studentSummaryService: StudentSummaryService,
        private loginService: LoginService,
        private assignmentService: AssignmentService,
        private parentPortalService: Student360Service,
        private classroom360Service: Classroom360Service,
        private router: Router
    ) {
    }

    ngOnInit() {
        this.card = this.data;
        if (this.card.display != 'Off') {
            const schoolIdStream = interval(100).subscribe(x => {
                if (this.studentService.studentEnrollment !== undefined) {
                    this.getCurrentGrades();
                    schoolIdStream.unsubscribe();
                }
            });
        }
        this.getPortalInfo();
    }

    ngOnDestroy(): void {
    }

    viewAllGradesClick() {
        this.router.navigateByUrl('/student360/reportCardGrades');
    }

    assignmentClick(studentGrades: CardStudentGrades) {
        this.assignmentService.selectedGP = studentGrades.TermName;
        this.assignmentService.selectedSectionId = studentGrades.Section_ID;
        this.assignmentService.selectedCoursebyCard = studentGrades.CourseName;
        this.assignmentService.filterbyCard = true;
        this.selectedCourse.Course = studentGrades.CourseName;
        this.selectedCourse.Id = studentGrades.Course_ID;
        this.assignmentService.selectedCourse = this.selectedCourse;
        this.assignmentService.selectedCourseSection = studentGrades.CourseNumber;
        this.router.navigateByUrl('/student360/assignments');
    }

    standardsClick(gradeRecord: CardStudentGrades) {
        this.studentService.selectionIndex = 1;
        this.studentService.StandradTabByCurrentGrade = true;
        // V9 ONE-6156
        this.studentService.selectedCardCourse = gradeRecord;
        this.router.navigateByUrl('/student360/reportCardGrades');
    }

    isDisabled() {
        if (this.card.disabled) {
            return 'disabledColor';
        } else {
            return 'enabledColor';
        }
    }

    getCurrentGrades() {
        const schoolId = this.studentService.studentEnrollment != null ? this.studentService.studentEnrollment.SchoolId : 0;
        this.studentSummaryService.getCardStudentGrades(this.studentService.selectedStudent.Id, schoolId, this.yearService.student360Year.AYIdentifier, this.gradingPeriodId, this.yearService.student360Year.Id).subscribe(response => {
            if (response.ok) {
                if (response.body.length > 0) {
                    this.currentTerm = response.body[0].TermName;
                } else {
                    this.currentTerm = 'No Grading Period';
                }
                this.studentGrades = response.body;
                this.angularMatData.data = this.studentGrades;
            }
        });
    }

    getPortalInfo() {
        if (this.userService.userType == 1) {
            this.staffAssignedPortalsSubscription = this.classroom360Service.observableStaffAssignedPortalsStream$.subscribe(portal => {
                if (portal.filter(x => x.PortalName == 'Assignments' && x.Disabled == false) != null
                    && portal.filter(x => x.PortalName == 'Assignments' && x.Disabled == false).length > 0) {
                    this.showAssignmentLink = true;
                }
            });
        } else {
            this.parentPortalSubscription = this.parentPortalService.observableParentAssignedPortalsStream$.subscribe(portal => {
                if (portal.filter(x => x.PortalName == 'Assignments' && x.Disabled == false) != null
                    && portal.filter(x => x.PortalName == 'Assignments' && x.Disabled == false).length > 0) {
                    this.showAssignmentLink = true;
                }
            });
        }
    }

    gradeDisplayString(gradeRecord: any) {
        // A Score of -1 means there is no Score available
        // A Grade of '' means there is no Grade available
        // If there is only a grade
        if (gradeRecord.Score == -1 && gradeRecord.Grade != '') {
            return gradeRecord.Grade;
        }
        // If there is only a Score
        if (gradeRecord.Score != -1 && gradeRecord.Grade == '') {
            return gradeRecord.Score + '%';
        }
        // If there is a score and a grade
        if (gradeRecord.Score != -1 && gradeRecord.Grade != '') {
            return gradeRecord.Score + '% ' + '(' + gradeRecord.Grade + ')';
        }
    }

    // ----------Angular Material------------------------------------
    // These methods are used to allow calls to the TylerMatTableDataSource methods from the html
    toggleExpanded(row: any) {
        // Toggles row between expanded and collapsed
        this.angularMatData.toggleExpanded(row);
    }

    checkExpanded(row: any): boolean {
        // Return true if expanded and false if collapsed
        return this.angularMatData.checkExpanded(row);
    }

    expandAll(expand: boolean) {
        //  Expand or collapse all rows
        //  true => expand all    false => collapse all
        this.angularMatData.expandAll(expand);
    }

    rowKeyPress(event: KeyboardEvent, row: any) {
        this.angularMatData.rowKeyPress(event, row);
    }
}
